import clsx from "clsx";
import { useEffect, useRef } from "react";

import { useAuth } from "@voxmedia/duet-context/context/auth-context";
import {
  NotificationsFilter,
  useNotificationsStore,
  useOptionalNotificationsDrawerContext,
} from "@voxmedia/duet-context/context/notifications-context";
import CoralCloseIcon from "@voxmedia/duet-framework/coral/CoralCloseIcon";
import VergeLogo from "@voxmedia/duet-ui/components/brand/theverge/VergeLogo";
import Drawer from "@voxmedia/duet-ui/components/navigation/Drawer/Drawer";
import LoginMessage from "@voxmedia/duet-ui/components/notifications/LoginMessage/LoginMessage";
import NotificationsContent, {
  ContentMode,
} from "@voxmedia/duet-ui/components/notifications/NotificationsContent/NotificationsContent";
import vergeLight from "@voxmedia/duet-ui/themes/theverge-light.css";

import * as styles from "./VergeNotificationsDrawerAppWrapper.css";

const VergeNotificationsDrawerRoot = () => {
  const notificationsDrawer = useOptionalNotificationsDrawerContext();
  const { user } = useAuth();
  const { RootStore } = useNotificationsStore();
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);

  const hasLoadedInitialNotifications = useRef<boolean>(false);
  const prevIsNotificationsOpen = useRef<boolean>(false);
  const isDrawerOpen = notificationsDrawer?.isDrawerOpen ?? false;
  const closeDrawer = notificationsDrawer?.closeDrawer ?? (() => {});

  const unseenCount =
    RootStore?.notificationsStore.notificationsStatus.unseen ?? 0;

  useEffect(() => {
    if (!RootStore?.userStore.viewer || RootStore.userStore.viewerLoading) {
      return;
    }

    const isOpening = !prevIsNotificationsOpen.current && isDrawerOpen;
    prevIsNotificationsOpen.current = isDrawerOpen;
    if (!isOpening) {
      return;
    }

    // The drawer delays notification fetches until the user opens it, then only
    // refreshes an already-loaded drawer when there is unseen work to reconcile.
    if (!hasLoadedInitialNotifications.current) {
      void RootStore.allNotificationsStore.loadNotificationsForUser();
      void RootStore.unreadNotificationsStore.loadNotificationsForUser({
        filter: NotificationsFilter.Unread,
      });
      hasLoadedInitialNotifications.current = true;
    } else if (unseenCount > 0) {
      void RootStore.allNotificationsStore.refreshNotifications();
      void RootStore.unreadNotificationsStore.refreshNotifications({
        filter: NotificationsFilter.Unread,
      });
    }
  }, [RootStore, isDrawerOpen, unseenCount]);

  if (!notificationsDrawer) {
    return null;
  }

  return (
    <Drawer
      drawerClassName={clsx(vergeLight, styles.drawer)}
      onClose={closeDrawer}
      drawerId="verge-notifications-drawer"
      aria-label="Notifications Drawer"
      initialFocusElement={closeButtonRef.current}
      open={isDrawerOpen}
    >
      <div className={styles.container}>
        <button
          type="button"
          className={styles.closeButton}
          aria-label="Close"
          onClick={closeDrawer}
          ref={closeButtonRef}
        >
          <CoralCloseIcon />
        </button>
        <div className={styles.content}>
          {user ? (
            <NotificationsContent
              showGoToNotificationsPageLink
              mode={ContentMode.Modal}
            />
          ) : (
            <LoginMessage Logo={VergeLogo} showCloseButton={false} />
          )}
        </div>
      </div>
    </Drawer>
  );
};

export default VergeNotificationsDrawerRoot;
